import { Injectable, signal } from '@angular/core';

import { parseRgb, solveFilterForRgb } from './image-filter';

export type ThemeName = 'dark' | 'light';

const STORAGE_KEY = 'pryspace:theme';
const FILTER_STYLE_ID = 'pryspace-accent-filter';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly current = signal<ThemeName>('dark');
  readonly accentFilter = signal<string | null>(null);

  init(): void {
    const stored = (typeof localStorage !== 'undefined'
      ? localStorage.getItem(STORAGE_KEY)
      : null) as ThemeName | null;
    this.apply(stored ?? 'dark');
  }

  toggle(): void {
    this.apply(this.current() === 'dark' ? 'light' : 'dark');
  }

  set(theme: ThemeName): void {
    this.apply(theme);
  }

  private apply(theme: ThemeName): void {
    this.current.set(theme);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', theme);
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, theme);
    }
    // Re-solve the CSS filter that tints a black PNG to the new accent
    // colour. Defer so the CSS variables have repainted first.
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => this.refreshAccentFilter());
    }
  }

  /**
   * Read `--accent` from the live computed style, solve a CSS-filter chain
   * that turns black into that accent, and inject it as `.img-color { ... }`
   * so any `<img class="img-color">` (e.g. the logo) is tinted in real time.
   *
   * Same trick used in apps/pryaz — see Priminity's
   * `libs/shared/environments/.../utility/imagefilter.class.ts`.
   */
  refreshAccentFilter(): void {
    if (typeof document === 'undefined') return;
    const accentVar = getComputedStyle(document.documentElement)
      .getPropertyValue('--accent')
      .trim();
    if (!accentVar) return;
    const rgb = parseRgb(accentVar);
    if (!rgb) return;

    const filter = solveFilterForRgb(rgb[0], rgb[1], rgb[2]);
    this.accentFilter.set(filter);

    // Make the filter available both as a CSS custom property AND as a
    // pre-baked `.img-color` rule, so callers can use either approach.
    document.documentElement.style.setProperty('--accent-filter', filter);

    let style = document.getElementById(FILTER_STYLE_ID) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = FILTER_STYLE_ID;
      document.head.appendChild(style);
    }
    style.textContent = `.img-color { filter: ${filter}; }`;
  }
}
